import { Entity } from '../types';

/**
 * Map data-flow decisions, extracted as PURE functions so they can be unit
 * tested. These guard a recurring regression: the map must show only the
 * relevant results during a search/trip, and the full ~5k-site set only in the
 * default browse view.
 */

/** Merge two entity lists by id; `primary` wins on conflict (preserves order). */
export function combineUniqueEntities(primary: Entity[], secondary: Entity[]): Entity[] {
  const byId = new Map<number, Entity>();
  for (const e of primary || []) {
    if (e && e.id != null) byId.set(e.id, e);
  }
  for (const e of secondary || []) {
    if (e && e.id != null && !byId.has(e.id)) byId.set(e.id, e);
  }
  return Array.from(byId.values());
}

/**
 * Whether the map should load & cluster ALL sites (default browse) vs show only
 * the current results. All-points is ONLY for the idle browse view — never
 * during a text search, a type filter, a route, or trip mode.
 */
export function shouldShowAllPoints(s: {
  mapViewMode: string;
  isRouteSearch: boolean;
  searchQuery: string;
  selectedType: string;
  /** A nationwide feature-filtered search (Filters + Search, no place) is showing. */
  featureSearch?: boolean;
}): boolean {
  return (
    s.mapViewMode !== 'trip' &&
    !s.isRouteSearch &&
    !(s.searchQuery || '').trim() &&
    s.selectedType === 'All' &&
    !s.featureSearch
  );
}

/**
 * The results count for the explorer's results bar / map badges, or `null`
 * while it isn't known yet. In the browse view the real figure is the
 * all-points total (~5k), which arrives with the map (desktop) or a deferred
 * fetch (mobile). Before that, falling through to searchTotal or the featured
 * page size showed "0 results" then "200 results" before jumping to the real
 * total, so callers render a neutral loading label instead.
 * `allPointsGaveUp` stops the wait if the all-points load never reports.
 */
export function resolveResultsCount(s: {
  searchAsMove: boolean;
  boundsCount: number;
  showAllPoints: boolean;
  allPointsTotal: number;
  allPointsGaveUp: boolean;
  searchTotal: number;
  mapEntitiesCount: number;
}): number | null {
  if (s.searchAsMove && s.boundsCount > 0) return s.boundsCount;
  if (s.showAllPoints) {
    if (s.allPointsTotal > 0) return s.allPointsTotal;
    if (!s.allPointsGaveUp) return null;
  }
  if (s.searchTotal > 0) return s.searchTotal;
  return s.mapEntitiesCount;
}

/**
 * The full number of matches when the count on screen is only a capped page
 * (a bounds search returns at most `limit` sites; an Australia-wide search
 * matches ~6.5k). `null` when what's shown is everything, so callers say
 * "6,513 results · showing 200" only when it's true.
 */
export function resolveMatchingTotal(s: {
  searchAsMove: boolean;
  boundsCount: number;
  /** total_matching from the last "Update as I move" search, when truncated. */
  boundsMatching: number;
  showAllPoints: boolean;
  allPointsTotal: number;
  searchTotal: number;
  /** total_matching from the last search, when truncated. */
  searchMatching: number;
}): number | null {
  if (s.searchAsMove && s.boundsCount > 0) {
    return s.boundsMatching > s.boundsCount ? s.boundsMatching : null;
  }
  if (s.showAllPoints && s.allPointsTotal > 0) return null;
  if (s.searchTotal > 0 && s.searchMatching > s.searchTotal) return s.searchMatching;
  return null;
}

/**
 * The results bar / map counter text when the results are a capped page.
 * Untyped: lead with the real total and say how many are shown. With a type
 * chip on, the typed count only covers the shown page, so it says so.
 */
export function truncatedCountDisplay(s: {
  /** The number the bar would print (shown results, or the typed count). */
  count: number;
  /** resolveMatchingTotal(); null = not truncated. */
  matching: number | null;
  /** How many results are actually loaded (the capped page). */
  shown: number;
  typeFiltered: boolean;
}): { count: number; note: string | null } {
  if (s.matching == null || s.shown <= 0) return { count: s.count, note: null };
  if (s.typeFiltered) return { count: s.count, note: `in the ${s.shown.toLocaleString('en-AU')} shown` };
  return { count: s.matching, note: `showing ${s.shown.toLocaleString('en-AU')}` };
}

/**
 * Whether the results list should show its loading skeleton. Covers the
 * featured first page as well as searches: before it arrives the list is
 * empty, and an empty list used to render "No results found" for the first
 * few seconds of every visit.
 */
export function isResultsListLoading(s: {
  mapViewMode: string;
  isSearching: boolean;
  isLoadingFeatured: boolean;
  isRouteSearch: boolean;
  searchQuery: string;
  resultCount: number;
}): boolean {
  if (s.mapViewMode === 'trip') return false;
  if (s.isSearching) return true;
  return (
    s.isLoadingFeatured &&
    !s.isRouteSearch &&
    !(s.searchQuery || '').trim() &&
    s.resultCount === 0
  );
}

/**
 * The entity set the supercluster should render. With allPoints on (browse),
 * prefer the detailed results then fill in every other site; otherwise show
 * ONLY the results (search/trip). This is the exact line that regressed.
 */
export function selectMapEntities(opts: {
  allPoints: boolean;
  richer: Entity[];
  allMapPoints: Entity[];
}): Entity[] {
  const { allPoints, richer, allMapPoints } = opts;
  if (allPoints && (allMapPoints?.length ?? 0) > 0) {
    return combineUniqueEntities(richer || [], allMapPoints);
  }
  return richer || [];
}

/**
 * The number + noun on the results bar and the map's count chip. In trip view
 * the noun is "trip stops", so the number must be the trip's stop count — it
 * used to print the campsites-along-the-route total ("46 trip stops" over a
 * 6-stop trip).
 */
export function mapCountSummary(s: {
  mapViewMode: string;
  /** tripPlaceCount(tripStops) — a round trip's return home isn't a stop. */
  tripStopCount: number;
  hasBuiltTrip: boolean;
  resultsCount: number | null;
  selectedType: string;
}): { count: number | null; label: string } {
  if (s.mapViewMode === 'trip' && s.tripStopCount > 0) {
    return { count: s.tripStopCount, label: `trip stop${s.tripStopCount === 1 ? '' : 's'}` };
  }
  const n = s.resultsCount ?? 0;
  if (s.hasBuiltTrip) {
    // These are campsites found along the drive, not stops on the trip.
    return { count: s.resultsCount, label: `campsite${n === 1 ? '' : 's'} along your route` };
  }
  return { count: s.resultsCount, label: s.selectedType === 'Accommodation' ? 'accommodation' : 'results' };
}

/**
 * The point halfway along a path, by distance, not its middle vertex. The
 * popular-trip name tags hang here: a trip's middle vertex is a town, and
 * the tag used to sit on that town's name.
 */
export function pathMidpoint(path: { lat: number; lng: number }[]): { lat: number; lng: number } | null {
  if (!path.length) return null;
  if (path.length === 1) return path[0];
  const segs: number[] = [];
  let total = 0;
  for (let i = 1; i < path.length; i++) {
    const d = Math.hypot(path[i].lat - path[i - 1].lat, path[i].lng - path[i - 1].lng);
    segs.push(d);
    total += d;
  }
  if (total === 0) return path[0];
  let left = total / 2;
  for (let i = 0; i < segs.length; i++) {
    if (left <= segs[i]) {
      const f = segs[i] ? left / segs[i] : 0;
      return {
        lat: path[i].lat + (path[i + 1].lat - path[i].lat) * f,
        lng: path[i].lng + (path[i + 1].lng - path[i].lng) * f,
      };
    }
    left -= segs[i];
  }
  return path[path.length - 1];
}

/**
 * The (fractional) Web Mercator zoom at which a lat/lng box fits a
 * width x height pixel area, capped at 12. Used to ease the empty planner's
 * map over to the trip being pointed at.
 */
export function zoomToFit(b: {
  width: number;
  height: number;
  north: number;
  south: number;
  east: number;
  west: number;
}): number {
  const w = Math.max(1, b.width);
  const h = Math.max(1, b.height);
  const latRad = (lat: number) => {
    const sin = Math.sin((lat * Math.PI) / 180);
    return Math.log((1 + sin) / (1 - sin)) / 2;
  };
  const latFrac = Math.max(1e-6, Math.abs(latRad(b.north) - latRad(b.south)) / (2 * Math.PI));
  const lngFrac = Math.max(1e-6, Math.abs(b.east - b.west) / 360);
  return Math.min(Math.log2(h / 256 / latFrac), Math.log2(w / 256 / lngFrac), 12);
}

/** The point a fraction `f` (0..1) of the way along a path, by distance. */
export function pathPointAt(path: { lat: number; lng: number }[], f: number): { lat: number; lng: number } | null {
  if (path.length < 2) return path[0] ?? null;
  const segs: number[] = [];
  let total = 0;
  for (let i = 1; i < path.length; i++) {
    const d = Math.hypot(path[i].lat - path[i - 1].lat, path[i].lng - path[i - 1].lng);
    segs.push(d);
    total += d;
  }
  let left = Math.max(0, Math.min(1, f)) * total;
  for (let i = 0; i < segs.length; i++) {
    if (left <= segs[i]) {
      const t = segs[i] ? left / segs[i] : 0;
      return { lat: path[i].lat + (path[i + 1].lat - path[i].lat) * t, lng: path[i].lng + (path[i + 1].lng - path[i].lng) * t };
    }
    left -= segs[i];
  }
  return path[path.length - 1];
}


type LL = { lat: number; lng: number };

/**
 * The drawn road, tied to the trip's first and last stops: a routed line
 * starts on the nearest road, which can sit a few hundred metres (or, for a
 * city centre, a few km) off the stop's own point, so the start pin floated
 * beside the line. When an end is further than `minKm` from its stop, the
 * stop's point is added as that end's vertex. Pure; returns the same array
 * when nothing needs adding.
 */
export function anchorPathEnds(path: LL[], start: Partial<LL> | null | undefined, end: Partial<LL> | null | undefined, minKm = 0.05): LL[] {
  if (!path || path.length < 2) return path;
  const located = (p: Partial<LL> | null | undefined): p is LL =>
    !!p && typeof p.lat === 'number' && typeof p.lng === 'number' && Number.isFinite(p.lat) && Number.isFinite(p.lng);
  const km = (a: LL, b: LL) => {
    const R = 6371;
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLng = ((b.lng - a.lng) * Math.PI) / 180;
    const x = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(x));
  };
  const head = located(start) && km(start, path[0]) > minKm ? [{ lat: start.lat, lng: start.lng }] : [];
  const tail = located(end) && km(end, path[path.length - 1]) > minKm ? [{ lat: end.lat, lng: end.lng }] : [];
  if (!head.length && !tail.length) return path;
  return [...head, ...path, ...tail];
}
